import { type PortalSettings, type FieldValues } from '@gorilla/portal-shared/src/schemas';
import { type DetailViewFieldDescriptor, type SectionDescriptor } from '@gorilla/portal-shared/src/types';
import { Box, Flex, Icon, Toggle, Text, Tooltip } from '@vibe/core';
import { Status as StatusIcon, LearnMore as LearnMoreIcon } from '@vibe/icons';
import { useBoardSourceResolver } from '../../hooks/use-board-source-resolver';
import { monday } from '../../services/monday';

type DetailViewFieldValue = { enabled?: boolean };

type DetailViewFieldProps = {
  value: DetailViewFieldValue | undefined;
  fieldDescriptor: DetailViewFieldDescriptor;
  portalSettings: PortalSettings;
  sectionDescriptor: SectionDescriptor;
  sectionFieldValues: FieldValues;
  onChange: (value: DetailViewFieldValue | undefined) => void;
  clientBoardSubitemsBoardId: number | null;
};

export function DetailViewField({
  value,
  fieldDescriptor,
  portalSettings,
  sectionDescriptor,
  sectionFieldValues,
  clientBoardSubitemsBoardId,
  onChange,
}: DetailViewFieldProps) {
  const { board, isLoading, boardId } = useBoardSourceResolver({
    portalSettings,
    sectionDescriptor,
    clientBoardSubitemsBoardId,
    sectionFieldValues,
  });

  if (!boardId || isLoading || !board) {
    return null;
  }

  return (
    <Box
      border={true}
      borderColor="layoutBorderColor"
      className="section__field section__field--itemdetails section__field--nomargin item--actions--wrapper"
    >
      <Box backgroundColor="greyBackgroundColor">
        <Box padding="medium">
          <Flex
            gap={24}
            style={{
              height: '30px',
            }}
          >
            <Flex gap={8}>
              <Icon icon={StatusIcon} size={18} style={{ marginRight: '3px', transform: 'translate3d(1px,0,0)' }} />
              <Text type="text2" weight="bold" align="start" ellipsis={true}>
                Item details
              </Text>
              <div>
                <Tooltip
                  title="What's this?"
                  content="Let clients open a panel with more item columns shown. Set the visibility for each column above. Click to learn more."
                  moveBy={{ main: 5, secondary: -15 }}
                  position="right"
                  showOnDialogEnter={false}
                >
                  <div
                    onClick={() => {
                      monday.execute('openLinkInTab', {
                        url: 'https://getgorilla.app/products/client-portal/docs/user-guides/item-details',
                      });
                    }}
                    style={{
                      cursor: 'pointer',
                    }}
                  >
                    <Icon icon={LearnMoreIcon} size={16} style={{ transform: 'translate3d(0,2px,0)' }} />
                  </div>
                </Tooltip>
              </div>
            </Flex>
            <Box style={{ marginLeft: 'auto' }}>
              <Flex
                justify="start"
                gap={10}
                style={{
                  flex: 1,
                }}
              >
                <Box
                  style={{
                    marginRight: 'auto',
                  }}
                >
                  <Flex style={{ margin: '0 -8px' }}>
                    <Toggle
                      offOverrideText=""
                      onOverrideText=""
                      size="small"
                      isSelected={!!value?.enabled}
                      onChange={(isSelected) => {
                        onChange({ ...(value || {}), enabled: isSelected });
                      }}
                    />
                  </Flex>
                </Box>
              </Flex>
            </Box>
          </Flex>
        </Box>
      </Box>
    </Box>
  );
}
